// The right-hand pane on desktop: the live claim log, every decision and the evidence it rests on.
// This is where the theory is visible: two primitives, one law, and visibility by audience.

import type { Claim, Decision, TraceNode } from '@model/index.ts';
import { html } from './html.ts';
import type { Trusted } from './html.ts';
import { receiptBody, world as w } from './world.ts';
import type { Audit, Check, Submission } from './world.ts';
import { attempt, describe } from './describe.ts';
import { compared, explain, labelled, reason } from './explain.ts';
import { canSee, claim, seqOf } from './queries.ts';
import { fmtDateTime } from './format.ts';

let mode: 'all' | 'seat' = 'all';
let seen = 0;
let audit: Audit | 'running' | null = null;
export const setAudit = (a: Audit | 'running' | null): void => {
  audit = a;
};
export const setRecordMode = (m: 'all' | 'seat'): void => {
  mode = m;
};

const short = (id: string): string => `${id.slice(0, 8)}…`;
const first = (id: string): string => w.name(id, 'en').split(' ')[0] ?? '';

function audience(c: Claim): string {
  if (c.audience === 'scope') return 'seen by everyone';
  return `seen only by ${c.audience.map((a) => w.name(a, 'en')).join(', ')}`;
}

function evidenceLink(id: string): Trusted {
  const c = claim(w, id);
  const seq = seqOf(w, id);
  if (!c || seq === undefined) return html`<span class="ev">${short(id)}</span>`;
  if (mode === 'seat' && !canSee(w, w.me.id, c)) return html`<span class="ev ev-hidden">a record ${first(w.me.id)} cannot see</span>`;
  return html`<button type="button" class="ev" data-act="jump" data-id="${id}">#${seq} ${c.predicate}</button>`;
}

// The kernel's own trace, by clause label (R1), with the values each clause compared (R2) and its evidence.
// Nested clauses (inside a limit or a branch) are indented under the clause that used them.
function trace(d: Decision): Trusted {
  const list = labelled(d.trace);
  if (!list.length) return html``;
  const depth = (n: TraceNode): number => list.filter((m) => m !== n && n.path.startsWith(`${m.path}.`)).length;
  const why = reason(d);
  return html`<ol class="rec-trace">
    ${list.map((n) => {
      const ids = n.matched ?? [];
      const vals = compared(n);
      return html`<li class="${n.ok ? 'ok' : 'no'} ${n === why ? 'reason' : ''}" style="--depth:${depth(n)}">
        <span class="mark" aria-label="${n.ok ? 'met' : 'not met'}">${n.ok ? '✓' : '✗'}</span>
        <span><code>${n.label}</code>${vals ? html` <span class="rec-val">${vals}</span>` : ''}${
          ids.length ? html` <span class="rec-ev">${ids.slice(0, 6).map(evidenceLink)}${ids.length > 6 ? ` +${ids.length - 6}` : ''}</span>` : ''
        }${n.hidden ? html` <span class="ev ev-hidden">+${n.hidden} hidden from this viewer</span>` : ''}</span></li>`;
    })}
  </ol>`;
}

function receiptLine(s: Submission): Trusted {
  const r = w.receiptOf(s);
  const b = r ? receiptBody(r) : null;
  if (!r || !b) return html``;
  return html`<p class="rec-meta rec-receipt">receipt <b>${b.n}</b> · signed by ${w.name(r.author, 'en')} · ${b.admitted ? 'admitted' : b.reason}${
    r.refs[1] ? html` · chained to <code title="${r.refs[1]}">${short(r.refs[1])}</code>` : ' · first in the chain'
  } · <code title="${r.id}">${short(r.id)}</code></p>`;
}

function submissionRow(s: Submission, index: number): Trusted {
  const c = s.claim;
  const admitted = s.decision.admitted;
  const seq = admitted ? seqOf(w, c.id) : undefined;
  const visible = admitted ? canSee(w, w.me.id, c) : c.author === w.me.id;
  const genesis = index === 0;
  const met = labelled(s.operator.trace).filter((n) => !n.path.includes('.n.') && !n.path.includes('.if'));
  const why = admitted ? null : explain(w, c.predicate, s.operator, c.author, 'en');
  const failed = admitted ? null : reason(s.operator);
  return html`<li id="rec-${c.id}" class="rec ${admitted ? 'admitted' : 'refused'} ${visible ? '' : 'dim'} ${index >= seen && seen > 0 ? 'fresh' : ''}" data-predicate="${c.predicate}">
    <div class="rec-top">
      <span class="rec-seq">${seq !== undefined ? `#${seq}` : 'no number'}</span>
      <time>${fmtDateTime(s.at, 'en')}</time>
      <span class="rec-verdict">${admitted ? 'Admitted' : 'Refused'}</span>
    </div>
    <p class="rec-line">${admitted ? describe(w, c) : attempt(w, c)}</p>
    ${why ? html`<p class="rec-why">${why.summary}</p>` : ''}
    <p class="rec-meta"><code>${c.predicate}</code> · ${admitted ? audience(c) : 'not on the record, so it has no effect'} · <code title="${c.id}">${short(c.id)}</code>${visible ? '' : html` · <span>not visible to ${first(w.me.id)}</span>`}</p>
    ${receiptLine(s)}
    ${genesis
      ? html`<p class="rec-meta">Genesis: the community’s first policy, admitted by definition.</p>`
      : met.length
        ? html`<details><summary>${admitted ? (met.length === 1 ? 'The 1 condition is met' : `All ${met.length} conditions met`) : html`Failed at <code>${failed?.label ?? s.operator.reason}</code>`}</summary>${trace(s.operator)}</details>`
        : ''}
  </li>`;
}

function checkRow(k: Check): Trusted {
  return html`<li class="rec check ${k.viewer === w.me.id ? '' : 'dim'}">
    <div class="rec-top"><span class="rec-seq">check</span><time>${fmtDateTime(k.at, 'en')}</time><span class="rec-verdict">Not a claim</span></div>
    <p class="rec-line">${w.name(k.viewer, 'en')} was shown, before trying: “${k.summary}”</p>
    <p class="rec-meta"><code>would(${k.predicate})</code> · capability is asked, never stored</p>
    <details><summary>As ${first(k.viewer)} was told: failed at <code>${reason(k.decision)?.label ?? k.decision.reason}</code></summary>${trace(k.decision)}</details>
  </li>`;
}

function witnessLine(): Trusted {
  if (!w.scope.witness) return html`<p class="rec-witness">Unwitnessed scope: decisions carry no receipts.</p>`;
  const replay = w.replayed ? html` Reloaded on this device by replay: ${w.replayed.matched} of ${w.replayed.receipts} receipts reproduced.` : '';
  const result =
    audit === 'running'
      ? html` <span role="status">Auditing…</span>`
      : audit
        ? html` <span role="status" data-testid="audit" class="${audit.ok ? 'ok' : 'bad'}">${audit.ok ? `Audit passed: ${audit.receipts} receipts; ${audit.detail}.` : `Audit failed: ${audit.detail}`}</span>`
        : '';
  return html`<p class="rec-witness">${w.name(w.scope.witness, 'en')}’s key witnesses every submission with a chained receipt.${replay}${result}</p>`;
}

export function renderRecord(el: HTMLElement): void {
  const me = w.me;
  const subs = w.submissions.map((s, i) => ({ s, i }));
  const shown = subs.filter(({ s }) => mode === 'all' || (s.decision.admitted ? canSee(w, me.id, s.claim) : s.claim.author === me.id));
  const checks = w.checks.filter((k) => mode === 'all' || k.viewer === me.id);
  const rows: { at: number; html: Trusted }[] = [
    ...shown.map(({ s, i }) => ({ at: s.at, html: submissionRow(s, i) })),
    ...checks.map((k) => ({ at: k.at + 0.5, html: checkRow(k) })),
  ].sort((a, b) => b.at - a.at);
  const admitted = w.submissions.filter((s) => s.decision.admitted).length;
  const refused = w.submissions.length - admitted;
  const who = first(me.id) || me.name.en;
  const counts =
    mode === 'all'
      ? `${admitted} admitted · ${refused} refused · ${w.device.length} private messages on phones, never on the record`
      : `${who} can see ${shown.filter(({ s }) => s.decision.admitted).length} of ${admitted} admitted claims · ${shown.filter(({ s }) => !s.decision.admitted).length} refused attempts of their own`;
  const scroller = el.querySelector('.rec-list')?.scrollTop ?? 0;
  el.innerHTML = html`
    <header class="rec-head">
      <div class="rec-title">
        <h2>The record</h2>
        <button type="button" class="rec-audit" data-act="audit">Audit the chain</button>
      </div>
      <p>Every claim submitted to Gulmohar Colony, newest first, with what the rules decided.</p>
      <fieldset class="segmented small">
        <legend class="sr-only">Show</legend>
        <label><input type="radio" name="rec-mode" value="all" data-act="record-mode" ${mode === 'all' ? 'checked' : ''} /><span>Everything the server holds</span></label>
        <label><input type="radio" name="rec-mode" value="seat" data-act="record-mode" ${mode === 'seat' ? 'checked' : ''} /><span>Only what ${who} can see</span></label>
      </fieldset>
      <p class="rec-counts" data-testid="rec-counts">${counts}</p>
      ${witnessLine()}
    </header>
    <ol class="rec-list" aria-live="polite">${rows.map((r) => r.html)}</ol>
  `.value;
  const list = el.querySelector('.rec-list');
  if (list) list.scrollTop = scroller;
  seen = w.submissions.length;
}

export function jump(el: HTMLElement, id: string): void {
  const row = el.querySelector<HTMLElement>(`#rec-${CSS.escape(id)}`);
  if (!row) return;
  row.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  row.classList.remove('pulse');
  void row.offsetWidth;
  row.classList.add('pulse');
}
